import { Link } from 'react-router'
import { Button, Card, Icon, Meter, Pill, Stack, Text } from '@/design-system'
import { BentoGrid, BentoSpan } from '@/components/Bento'
import { CurrencyBadge } from '@/components/CurrencyBadge'
import { TransactionRow } from '@/components/TransactionRow'
import { ErrorState, Skeleton } from '@/components/States'
import { PageHeader } from '@/app/layouts/PageHeader'
import { useRepository } from '@/data/repository-context'
import { useQuery } from '@/data/use-query'
import { formatMoney, formatPercent, money } from '@/lib/money'
import styles from './DashboardScreen.module.css'

const greeting = (date = new Date()): string => {
  const hour = date.getHours()
  if (hour < 12) return 'Good morning'
  if (hour < 18) return 'Good afternoon'
  return 'Good evening'
}

const longDate = new Intl.DateTimeFormat('en-GB', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

export function DashboardScreen() {
  const repository = useRepository()
  const user = useQuery(() => repository.getUser(), [repository])
  const accounts = useQuery(() => repository.listAccounts(), [repository])
  const cards = useQuery(() => repository.listCards(), [repository])
  const transactions = useQuery(() => repository.listTransactions({ limit: 6 }), [repository])
  const spending = useQuery(() => repository.getSpendingBreakdown(), [repository])
  const cashflow = useQuery(() => repository.getCashflow(), [repository])

  const primary = accounts.data?.find((account) => account.primary)
  const cardSpend = cards.data?.reduce((sum, card) => sum + card.spentThisMonth.minorUnits, 0) ?? 0
  const peak = cashflow.data
    ? Math.max(...cashflow.data.flatMap((point) => [point.inbound.minorUnits, point.outbound.minorUnits]))
    : 1

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${user.data?.firstName ?? '…'}`}
        subtitle={`${longDate.format(new Date())} · everything is up to date`}
        actions={
          <>
            <Button variant="ghost" size="md" iconStart="swap_horiz" to="/transfers/send">
              Send abroad
            </Button>
            <Button variant="primary" size="md" iconStart="add" to="/add-money">
              Add money
            </Button>
          </>
        }
      />

      <BentoGrid>
        {/* Primary balance */}
        <BentoSpan span={2}>
          <Card tone="dark" className={styles.tile}>
            <Card.Header>
              <Text variant="eyebrow" weight="semibold" tone="onDarkMuted">
                Main account
              </Text>
              <Pill tone="accent">{formatPercent(0.024)}</Pill>
            </Card.Header>
            <Stack gap={2} className="pb-grow">
              {accounts.status === 'loading' && <Skeleton height={64} />}
              {primary && (
                <>
                  <Text as="p" variant="display" weight="bold" tone="onDark" numeric>
                    {formatMoney(primary.balance)}
                  </Text>
                  <Text variant="caption" tone="onDarkMuted">
                    {primary.label} · {primary.rail} · {primary.maskedNumber}
                  </Text>
                </>
              )}
            </Stack>
            <Card.Footer>
              <Stack direction="row" gap={2} wrap>
                <Button variant="primary" size="md" iconStart="south_west" to="/receive">
                  Receive
                </Button>
                <Button variant="onDark" size="md" iconStart="north_east" to="/transfers/send">
                  Send
                </Button>
                <Button variant="onDark" size="md" iconStart="currency_exchange" to="/convert">
                  Convert
                </Button>
              </Stack>
            </Card.Footer>
          </Card>
        </BentoSpan>

        {/* Accounts */}
        <Card className={styles.tile}>
          <Card.Header>
            <Text variant="caption" weight="semibold">
              Accounts
            </Text>
            <Pill tone="neutral">{accounts.data?.length ?? '—'}</Pill>
          </Card.Header>
          <Stack gap={3} className="pb-grow pb-mt-4">
            {accounts.data?.slice(0, 3).map((account) => (
              <Stack key={account.id} direction="row" align="center" gap={3}>
                <CurrencyBadge currency={account.currency} size={32} />
                <Stack grow gap={0}>
                  <Text variant="caption" weight="semibold" truncate>
                    {account.currency}
                  </Text>
                  <Text variant="eyebrow" tone="secondary">
                    {account.rail}
                  </Text>
                </Stack>
                <Text variant="caption" weight="semibold" numeric>
                  {formatMoney(account.balance, { compactDecimals: true })}
                </Text>
              </Stack>
            ))}
          </Stack>
          <Card.Footer>
            <Button variant="outline" size="sm" block iconEnd="arrow_forward" to="/accounts">
              All accounts
            </Button>
          </Card.Footer>
        </Card>

        {/* Cards */}
        <Card className={styles.tile}>
          <Card.Header>
            <Text variant="caption" weight="semibold">
              Cards
            </Text>
            <Pill tone="neutral">{cards.data?.length ?? '—'}</Pill>
          </Card.Header>
          <Stack gap={2} className="pb-grow pb-mt-4">
            <Text as="p" variant="heading" weight="bold" numeric>
              {formatMoney(money(cardSpend, 'USD'))}
            </Text>
            <Text variant="caption" tone="secondary">
              Spent this month across {cards.data?.length ?? 0} cards
            </Text>
            <Meter value={0.38} label="Share of monthly limit used" />
          </Stack>
          <Card.Footer>
            <Button variant="outline" size="sm" block iconEnd="arrow_forward" to="/cards">
              Manage cards
            </Button>
          </Card.Footer>
        </Card>

        {/* Cash flow */}
        <BentoSpan span={2}>
          <Card className={styles.tile}>
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Money in and out
              </Text>
              <div className={styles.legend}>
                <span className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: 'var(--pb-accent)' }} />
                  <Text variant="eyebrow" tone="secondary">
                    In
                  </Text>
                </span>
                <span className={styles.legendItem}>
                  <span className={styles.swatch} style={{ background: 'var(--pb-ink)' }} />
                  <Text variant="eyebrow" tone="secondary">
                    Out
                  </Text>
                </span>
              </div>
            </Card.Header>
            <div className="pb-mt-5">
              {cashflow.status === 'loading' && <Skeleton height={120} />}
              {cashflow.data && (
                <>
                  <div className={styles.chart}>
                    {cashflow.data.map((point) => (
                      <div key={point.label} className={styles.column}>
                        <div
                          className={`${styles.bar} ${styles.barIn}`}
                          style={{ height: `${(point.inbound.minorUnits / peak) * 100}%` }}
                          title={`In ${formatMoney(point.inbound, { compactDecimals: true })}`}
                        />
                        <div
                          className={`${styles.bar} ${styles.barOut}`}
                          style={{ height: `${(point.outbound.minorUnits / peak) * 100}%` }}
                          title={`Out ${formatMoney(point.outbound, { compactDecimals: true })}`}
                        />
                      </div>
                    ))}
                  </div>
                  <div className={styles.axis}>
                    {cashflow.data.map((point) => (
                      <Text key={point.label} variant="eyebrow" tone="secondary" className={styles.axisLabel}>
                        {point.label}
                      </Text>
                    ))}
                  </div>
                </>
              )}
            </div>
          </Card>
        </BentoSpan>

        {/* Spending */}
        <BentoSpan span={2}>
          <Card className={styles.tile}>
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Where it went
              </Text>
              <Text variant="eyebrow" tone="secondary">
                September
              </Text>
            </Card.Header>
            <Stack gap={4} className="pb-mt-4">
              {spending.status === 'error' && <ErrorState message="Spending is unavailable right now." />}
              {spending.data?.map((slice) => (
                <Stack key={slice.category} gap={2}>
                  <Stack direction="row" align="center" gap={3}>
                    <Icon name={slice.icon} size={16} />
                    <Text variant="caption" weight="semibold" className="pb-grow" truncate>
                      {slice.category}
                    </Text>
                    <Text variant="caption" tone="secondary" numeric>
                      {formatMoney(slice.amount, { compactDecimals: true })}
                    </Text>
                  </Stack>
                  <Meter value={slice.share} height={6} tone="ink" label={slice.category} />
                </Stack>
              ))}
            </Stack>
          </Card>
        </BentoSpan>

        {/* Recent activity */}
        <BentoSpan span={3}>
          <Card className={styles.tile}>
            <Card.Header>
              <Text variant="caption" weight="semibold">
                Recent activity
              </Text>
              <Button variant="ghost" size="sm" iconEnd="arrow_forward" to="/transactions">
                See all
              </Button>
            </Card.Header>
            <div className={`${styles.rows} pb-mt-3`}>
              {transactions.status === 'loading' && <Skeleton height={220} />}
              {transactions.data?.map((transaction) => (
                <TransactionRow key={transaction.id} transaction={transaction} />
              ))}
            </div>
          </Card>
        </BentoSpan>

        {/* Next steps */}
        <Stack gap={3}>
          {[
            { icon: 'account_balance', title: 'Add an account', body: 'Free to open, $50 first deposit', to: '/open/account' },
            { icon: 'credit_card', title: 'Add a card', body: 'Virtual $9 · physical $50', to: '/open/card' },
            { icon: 'savings', title: 'Open a Vault', body: 'Earns while it sits', to: '/invest' },
          ].map((item) => (
            <Card key={item.title} tone="plain" interactive padded as={Link} to={item.to}>
              <Stack direction="row" align="center" gap={3}>
                <Icon name={item.icon} size={20} />
                <Stack grow gap={0}>
                  <Text variant="caption" weight="semibold">
                    {item.title}
                  </Text>
                  <Text variant="eyebrow" tone="secondary">
                    {item.body}
                  </Text>
                </Stack>
                <Icon name="arrow_forward" size={16} />
              </Stack>
            </Card>
          ))}
        </Stack>
      </BentoGrid>
    </>
  )
}
